import { useCallback, useEffect, useState } from "react";

export function useWebcam(args: {
  videoRef: React.RefObject<HTMLVideoElement>;
  handleFileUpload?: (file: File) => void;
}) {
  const { handleFileUpload, videoRef } = args;

  const [showCamera, setShowCamera] = useState(false);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    navigator.permissions
      .query({ name: "camera" as PermissionName })
      .then((status) => {
        if (status.state === "denied") {
          setError("Camera access denied. Check browser settings.");
        }
      });
  }, []);

  useEffect(() => {
    return () => {
      const stream = videoRef.current?.srcObject as MediaStream | null;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [videoRef]);

  const onWebcamClick = useCallback(() => {
    setShowCamera(true);

    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput"
        );
        if (videoDevices.length === 0) {
          setError("No cameras found.");
          return;
        }
        setAvailableCameras(videoDevices);

        const backCamera = videoDevices.find((camera) => /back|rear/i.test(camera.label));

        const targetDeviceId = deviceId || backCamera?.deviceId || videoDevices[0]?.deviceId || null;

        setDeviceId(targetDeviceId);

        return navigator.mediaDevices.getUserMedia({
          video: targetDeviceId
            ? { deviceId: { exact: targetDeviceId } }
            : true,
        });
      })
      .then((stream) => {
        if (videoRef.current && stream) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      })
      .catch((err) => setError("Error accessing camera."));
  }, [deviceId, videoRef]);

  const switchCamera = useCallback(() => {
    if (availableCameras.length > 1) {
      const currentIndex = availableCameras.findIndex(
        (camera) => camera.deviceId === deviceId
      );
      const nextIndex = (currentIndex + 1) % availableCameras.length;
      const nextDeviceId = availableCameras[nextIndex].deviceId;

      setDeviceId(nextDeviceId);

      navigator.mediaDevices
        .getUserMedia({
          video: { deviceId: { exact: nextDeviceId } },
        })
        .then((stream) => {
          if (videoRef.current) {
            const oldStream = videoRef.current.srcObject as MediaStream;
            oldStream?.getTracks().forEach((track) => track.stop());

            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }
        })
        .catch((err) => setError("Error switching camera."));
    }
  }, [availableCameras, deviceId, videoRef]);

  const handleCloseCamera = useCallback(() => {
    const stream = videoRef.current?.srcObject as MediaStream | null;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setShowCamera(false);
  }, [videoRef]);

  const handleTakePhoto = useCallback(() => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (videoRef.current && context) {
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      return new Promise<File | null>((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], `${new Date().toISOString()}.jpg`, {
              type: "image/jpeg",
            });
            handleFileUpload?.(file);
            resolve(file);
            setShowCamera(false);
          } else {
            resolve(null);
          }
        }, "image/jpeg");
      });
    }

    return Promise.resolve(null);
  }, [handleFileUpload, videoRef]);

  return {
    onWebcamClick,
    switchCamera,
    showCamera,
    setShowCamera,
    handleCloseCamera,
    handleTakePhoto,
    error,
  };
}
